import axios, { AxiosError, AxiosRequestConfig } from 'axios';
import { JSend } from 'Models/Generics/BFF';
import { getApiDomain } from 'Utils/httpClients/httpUtils';
import { getSessionToken } from 'Utils/session';

type LoggerMethod = (message: string, breadcrumb: string, data?: Record<string, unknown>) => void;

interface Logger {
  debug: LoggerMethod;
  error: LoggerMethod;
  info: LoggerMethod;
  warn: LoggerMethod;
}

interface GenericClientConfig extends AxiosRequestConfig {
  logger?: Logger;
}


export default class GenericBffClient {
  #logger;

  prefix: string;

  baseURL: string;

  constructor(domainEnvVariable: string, prefix = '', allConfig: GenericClientConfig = {}) {
    const { logger } = allConfig;
    this.baseURL = getApiDomain(domainEnvVariable) ?? '';

    this.#logger = logger || console;

    if (!this.baseURL) this.#logger.error(`No baseUrl found for Env Variable ${domainEnvVariable}`, 'GenericBffClient');

    this.prefix = prefix;
  }

  get<T extends object = object>(path: string, config: AxiosRequestConfig = {}) {
    return this.#request<T>('get', path, config);
  }

  put<T extends object = object>(path: string, data: object, config: AxiosRequestConfig = {}) {
    return this.#request<T>('put', path, { ...config, data });
  }

  post<T extends object = object>(path: string, data: object, config: AxiosRequestConfig = {}) {
    return this.#request<T>('post', path, { ...config, data });
  }

  delete<T extends object = object>(path: string, config: AxiosRequestConfig = {}) {
    return this.#request<T>('delete', path, config);
  }

  #request = async <T extends object = object>(
    method: string,
    path: string,
    config: AxiosRequestConfig = {},
  ) => {
    const token = getSessionToken();
    const headers = {
      'Accept-Language': window.navigator.language,
      Authorization: `Bearer ${token}`,
      'Channel-Source': 'web',
    };
    const url = `${this.prefix}${path}`;
    if (!method || !path) throw new Error('GenericBffClient: #request requires a path');

    try {
      const response = await axios.request<JSend<T>>({
        ...config,
        baseURL: this.baseURL,
        headers,
        method,
        url,
      });

      return response?.data?.data ?? null;
    } catch (error) {
      const { response } = error as AxiosError;

      throw response?.data?.status === 'fail' ? response : error;
    }
  }
}
